import { Component, OnInit, Input, Output, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, EventEmitter, AfterViewInit, HostBinding, OnDestroy } from '@angular/core';
import { GridService } from '../grid.service';
import { ColumnDisplayType, GridConfiguration, PagedModel } from '../models';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTable } from '@angular/material/table';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
  standalone: false,
  selector: 'lib-report-grid',
  templateUrl: './report-grid.component.html',
  styleUrls: ['./report-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GridService]
})
export class ReportGridComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() dataSource: Observable<any[]>;
  @Input() count;
  @Input() initialized: boolean;
  @Input() loading: boolean;
  @Input() isModal: boolean;
  @Input() pagedModel: PagedModel;
  @Input() gridConfiguration: GridConfiguration;
  @Input() filterStringParameter: string;
  @Input() enableFilter: boolean = true; 
  @Input() clearFilterOnInitialLoad: boolean = false;
  @Input() hasPaginator: boolean = true;
  @Input() emptyMessage: boolean = false;
  @Input() spinner: boolean = false;
  @Output() pageChanged: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() sortFilterChanged: EventEmitter<PagedModel> = new EventEmitter<PagedModel>();
  @Output() selectionChanged: EventEmitter<any[]> = new EventEmitter<any[]>();

  @ViewChild('column') column: HTMLElement;
  private paginatorSubscription: Subscription;
  private sortSubscription: Subscription;

  private _paginator: MatPaginator;
  @ViewChild(MatPaginator)
  set paginator(paginator: MatPaginator) {
    if (!paginator || this._paginator === paginator) return;
    this._paginator = paginator;
    this.paginatorSubscription?.unsubscribe();
    this.paginatorSubscription = paginator.page.subscribe((event) => this.pageChanged.emit(event));
  }
  get paginator(): MatPaginator { return this._paginator; }

  private _sort: MatSort;
  @ViewChild(MatSort)
  set sort(sort: MatSort) {
    if (!sort || this._sort === sort) return;
    this._sort = sort;
    this.sortSubscription?.unsubscribe();
    this.sortSubscription = sort.sortChange.subscribe(() => {
      const pagedModel = this.gridService.buildPagedModel(null, sort, this.gridConfiguration, this.filterFormGroup);
      this.sortFilterChanged.emit(pagedModel);
    });
  }
  get sort(): MatSort { return this._sort; }
  @ViewChild(MatTable) grid: MatTable<any>;

  @HostBinding('class') gridClass = 'lib-report-grid';
  @HostBinding('class.mat-elevation-z2') get gridElevation() {
    return this.gridConfiguration?.elevation;
  }
  
  get hasFilters(): boolean {
    return this.gridConfiguration?.columnDefinitions?.some((x) => x.filterable === true) || false;
  }

  columnTypes = ColumnDisplayType;
  selection = new SelectionModel<any>(true, []);
  currentData: any[] = [];
  
  public filterFormGroup: UntypedFormGroup;
  subscriptions: Subscription[] = [];
  constructor(public gridService: GridService, public router: Router, public route: ActivatedRoute) {}

  ngOnInit(): void {
    this.filterFormGroup = new UntypedFormGroup({});

    if (this.dataSource) {
      this.subscriptions.push(
        this.dataSource.subscribe((data) => {
          this.currentData = data ?? [];
          this.selection.clear();
          this.selectionChanged.emit(this.selection.selected);
        })
      );
    }
  }
  
  ngAfterViewInit(): void {
    const filter$ = this.filterFormGroup.valueChanges
    .pipe(
      debounceTime(750),
      distinctUntilChanged(),
    ).subscribe((f) => 
    {
      // mayvbe why it takes a while to reload datasource results after cancel
      const pm = this.gridService.buildPagedModel(null, this.sort, this.gridConfiguration, this.filterFormGroup);
      this.sortFilterChanged.emit(pm);
    });

    this.subscriptions.push(filter$);
  }

  ngOnDestroy() {
      this.subscriptions.forEach((s) => s?.unsubscribe());
      this.paginatorSubscription?.unsubscribe();
      this.sortSubscription?.unsubscribe();
  }

  cellClick(event: MouseEvent, columnKey: string, rowValue: any, headerText?: string) {
    const columnDef = this.gridConfiguration.columnDefinitions.find((x) => x.displayKey === columnKey || x.id === columnKey);
    if (columnDef?.cellClickEvent) {
      event.stopPropagation();
      columnDef.cellClickEvent(rowValue, headerText);
    }
  }

  public camelCaseRemoveSpaces(str: string) {
    return (str.substring(0, 1).toLowerCase() + str.substring(1, str.length)).replace(' ', '');
  }
  trackByIndex(index: number, item: any) {
    return index
  }
  trackById(index: number, item: any) {
    return item.id
  }

  isAllSelected(): boolean {
    return this.currentData.length > 0 && this.selection.selected.length === this.currentData.length;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.currentData.forEach((row) => this.selection.select(row));
    this.selectionChanged.emit(this.selection.selected);
  }

  toggleCheckbox(row: any): void {
    this.selection.toggle(row);
    this.selectionChanged.emit(this.selection.selected);
  }
}
